/**
 * AI-Explain: build context for explainable UI elements (data-ai-explain).
 * Sent to the backend so the assistant can explain what the element does.
 */

/** App layout sections in ResAdmin: Header, SubHeader, Filters section, Listing grid. */
export type AppSection = 'header' | 'subheader' | 'filters' | 'listing' | null

export interface ElementExplainContext {
    url: string
    pathname: string
    domPath: string
    tagName: string
    id: string | null
    className: string | null
    ariaLabel: string | null
    title: string | null
    placeholder: string | null
    labelText: string | null
    visibleText: string | null
    dataAiExplain: string | null
    /** Which part of the app layout (Header, SubHeader, Filters, Listing grid). */
    appSection: AppSection
}

const MAX_VISIBLE_TEXT = 200

/**
 * Build a short DOM path (tag + id/class) from root to the element.
 */
function getDomPath(el: HTMLElement): string {
    const segments: string[] = []
    let current: HTMLElement | null = el
    while (current && current !== document.body) {
        const tag = current.tagName.toLowerCase()
        const id = current.id ? `#${current.id}` : ''
        const cls =
            current.className && typeof current.className === 'string'
                ? current.className
                      .trim()
                      .split(/\s+/)
                      .filter(Boolean)
                      .slice(0, 2)
                      .map((c) => `.${c}`)
                      .join('')
                : ''
        segments.unshift(`${tag}${id}${cls}`)
        current = current.parentElement
    }
    return segments.join(' > ')
}

/**
 * Get associated label text (for inputs: <label for="id"> or wrapping label).
 */
function getLabelText(el: HTMLElement): string | null {
    if (el.id) {
        const label = document.querySelector(`label[for="${el.id}"]`)
        if (label?.textContent?.trim()) return label.textContent.trim()
    }
    const parentLabel = el.closest('label')
    if (parentLabel?.textContent?.trim()) return parentLabel.textContent.trim()
    return null
}

/**
 * Infer app section from element or ancestors: header, subheader, filters, listing.
 * Walks up the DOM tree, so only the section root needs data-ai-explain-section;
 * children inherit the section from their ancestor.
 * Falls back to heuristics from class/id/role if no attribute is found.
 */
function getAppSection(el: HTMLElement): AppSection {
    let current: HTMLElement | null = el
    while (current && current !== document.body) {
        const section = current.getAttribute('data-ai-explain-section')?.trim().toLowerCase()
        if (section === 'header' || section === 'subheader' || section === 'filters' || section === 'listing')
            return section as AppSection
        const cls = (current.className && typeof current.className === 'string' ? current.className : '') + (current.id || '')
        const lower = cls.toLowerCase()
        if (lower.includes('subheader') || lower.includes('sub-header')) return 'subheader'
        if (lower.includes('header') && !lower.includes('sub')) return 'header'
        if (lower.includes('filter')) return 'filters'
        if (lower.includes('listing') || lower.includes('grid') || lower.includes('table') || lower.includes('datagrid')) return 'listing'
        const role = current.getAttribute('role')
        if (role === 'banner') return 'header'
        current = current.parentElement
    }
    return null
}

/**
 * Get visible text of the element (and maybe first level children), trimmed and truncated.
 */
function getVisibleText(el: HTMLElement): string | null {
    const text = el.textContent?.trim() ?? ''
    if (!text) return null
    const singleLine = text.replace(/\s+/g, ' ')
    return singleLine.length > MAX_VISIBLE_TEXT
        ? singleLine.slice(0, MAX_VISIBLE_TEXT) + '…'
        : singleLine
}

/**
 * Gather context for an element marked with data-ai-explain.
 */
export function getElementExplainContext(el: HTMLElement): ElementExplainContext {
    let ariaLabel = el.getAttribute('aria-label')?.trim() || null
    if (!ariaLabel && el.getAttribute('aria-labelledby')) {
        const id = el.getAttribute('aria-labelledby')
        ariaLabel = id
            ? document.getElementById(id)?.textContent?.trim() ?? null
            : null
    }
    const dataAiExplain = el.getAttribute('data-ai-explain')?.trim() || null
    const appSection = getAppSection(el)

    return {
        url: window.location.href,
        pathname: window.location.pathname,
        domPath: getDomPath(el),
        tagName: el.tagName.toLowerCase(),
        id: el.id?.trim() || null,
        className:
            (el.className && typeof el.className === 'string'
                ? el.className.trim()
                : null) || null,
        ariaLabel: ariaLabel || null,
        title: el.getAttribute('title')?.trim() || null,
        placeholder:
            (el as HTMLInputElement | HTMLTextAreaElement).placeholder?.trim() ||
            null,
        labelText: getLabelText(el),
        visibleText: getVisibleText(el),
        dataAiExplain,
        appSection,
    }
}

/**
 * Build the prompt string sent to the chat backend for explaining this element.
 */
export function buildExplainPrompt(context: ElementExplainContext): string {
    const sectionLabel =
        context.appSection === 'header'
            ? 'Header'
            : context.appSection === 'subheader'
              ? 'SubHeader'
              : context.appSection === 'filters'
                ? 'Filters section'
                : context.appSection === 'listing'
                  ? 'Listing grid'
                  : null

    const parts: string[] = [
        'Explain this UI element in the current application.',
        '',
        'App layout: Header, SubHeader, Filters section, Listing grid.',
        sectionLabel ? `This element is in: ${sectionLabel}.` : '',
        '',
        `URL: ${context.url}`,
        `Path: ${context.domPath}`,
        `Tag: ${context.tagName}`,
    ]
    if (context.id) parts.push(`ID: ${context.id}`)
    if (context.className) parts.push(`Class: ${context.className}`)
    if (context.ariaLabel) parts.push(`Aria-label: ${context.ariaLabel}`)
    if (context.title) parts.push(`Title: ${context.title}`)
    if (context.placeholder) parts.push(`Placeholder: ${context.placeholder}`)
    if (context.labelText) parts.push(`Label: ${context.labelText}`)
    if (context.visibleText) parts.push(`Visible text: ${context.visibleText}`)
    if (context.dataAiExplain)
        parts.push(`Hint (data-ai-explain): ${context.dataAiExplain}`)
    return parts.filter(Boolean).join('\n')
}
